import type { GenericOrganization, Organization, SimpleOrganization } from '@app/src/types/organizations'
import { ensureWebsiteClickability, normalizeWebsiteDisplay } from '@app/src/utils/helpers'
import { Config } from '../config'

/** Hostname only — Brandfetch Logo API `domain/{identifier}` expects a domain, not a path. */
export function brandfetchDomainFromWebsiteAddress(websiteAddress: string | null | undefined): string | undefined {
  const trimmed = websiteAddress?.trim()
  if (!trimmed) return undefined

  const clickable = ensureWebsiteClickability(trimmed)
  if (clickable) {
    try {
      const host = new URL(clickable).hostname.replace(/^www\./i, '').trim()
      if (host) return host
    } catch {
      /* fall through */
    }
  }

  const display = normalizeWebsiteDisplay(trimmed)
  if (!display) return undefined
  const hostOnly = display.split('/')[0]?.split('?')[0]?.split('#')[0]?.trim()
  return hostOnly || undefined
}

export type CompanyLogoResolveFields = {
  organizationImageUrl?: string | null | undefined
  providerImageUrl?: string | null | undefined
  websiteAddress?: string | null | undefined
}

export function resolveCompanyLogoImageUrlFromFields(fields: CompanyLogoResolveFields): string | undefined {
  if (fields.organizationImageUrl) return fields.organizationImageUrl
  if (fields.providerImageUrl) return fields.providerImageUrl
  const domain = brandfetchDomainFromWebsiteAddress(fields.websiteAddress)
  if (!domain) return undefined

  return `https://cdn.brandfetch.io/domain/${domain}/fallback/404/type/icon?c=${Config.BRANDFETCH_CLIENT_ID}`
}

export function resolveCompanyLogoImageUrl(
  company: GenericOrganization | Organization | SimpleOrganization,
): string | undefined {
  if ('linked' in company) {
    const provider = company as GenericOrganization
    return resolveCompanyLogoImageUrlFromFields({
      organizationImageUrl: provider.organization?.image?.url,
      providerImageUrl: provider.image?.url,
      websiteAddress: provider.websiteAddress,
    })
  }

  return resolveCompanyLogoImageUrlFromFields({
    organizationImageUrl: company.image?.url,
    providerImageUrl: undefined,
    websiteAddress: company.websiteAddress,
  })
}
